import axios from 'axios';

import { API_V1_BASE } from '../config/api.config';
import { STORAGE_KEYS } from '../config/app.config';

/**
 * کلاینت axios پیکربندی‌شده برای API v1 + Bearer Sanctum.
 */
export const http = axios.create({
  baseURL: API_V1_BASE,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 30_000,
});

function isImpersonationSession() {
  return typeof window !== 'undefined' && Boolean(window.localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN));
}

/** APIهای سطح پلتفرم — بدون هدر X-Organization-ID (مثل login-as کاربر) */
function shouldAttachOrgHeader(url) {
  if (!url || typeof url !== 'string') return true;
  const path = url.split('?')[0].replace(/^\//, '');
  if (/^users(\/|$)/.test(path)) return false;
  if (/^profile(\/|$)/.test(path)) return false;
  if (/^permissions(\/|$)/.test(path)) return false;
  if (/^platform(\/|$)/.test(path)) return false;
  if (/^auth\/me/.test(path)) {
    // بدون X-Organization-ID بک‌اند organization_permissions را [] برمی‌گرداند
    // و منوی سازمان (جلسات، اتاق‌ها، …) خالی می‌ماند.
    return true;
  }
  if (/^auth\/(logout|stop-impersonation|organizations|ensure-personal-workspace|personal-organization|login-panel|register)/.test(path)) {
    return false;
  }
  return true;
}

function isPlatformSuperAdminSession() {
  if (typeof window === 'undefined') return false;
  // هنگام impersonation همیشه سازمان فعال لازم است — حتی اگر کش کاربر قدیمی سوپرادمین باشد
  if (isImpersonationSession()) return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return false;
    const u = JSON.parse(raw);
    return u.is_platform_super_admin === true || u.requires_active_organization === false;
  } catch {
    return false;
  }
}

http.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) : null;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // FormData باید multipart با boundary مرورگر برود — نه application/json پیش‌فرض
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const h = config.headers;
    if (h && typeof h.set === 'function') {
      // axios: false = هدر را حذف کن تا boundary ست شود
      h.set('Content-Type', false);
    } else if (h && typeof h.delete === 'function') {
      h.delete('Content-Type');
    } else if (h && typeof h === 'object') {
      delete h['Content-Type'];
      delete h['content-type'];
    }
  }

  const orgId =
    typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEYS.CURRENT_ORG_ID) : null;
  if (orgId && shouldAttachOrgHeader(config.url) && !isPlatformSuperAdminSession()) {
    const h = config.headers;
    let existing;
    if (h && typeof h.get === 'function') {
      existing = h.get('X-Organization-ID');
    } else if (h && typeof h === 'object') {
      existing = h['X-Organization-ID'];
    }
    if (!existing) {
      if (h && typeof h.set === 'function') {
        h.set('X-Organization-ID', String(orgId));
      } else {
        config.headers = { ...(h || {}), 'X-Organization-ID': String(orgId) };
      }
    }
  }
  return config;
});

http.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && typeof window !== 'undefined') {
      const adminToken = window.localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN);
      window.localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      window.localStorage.removeItem(STORAGE_KEYS.USER);
      window.localStorage.removeItem(STORAGE_KEYS.ORGANIZATIONS);
      window.localStorage.removeItem(STORAGE_KEYS.CURRENT_ORG_ID);
      window.localStorage.removeItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN);
      window.localStorage.removeItem(STORAGE_KEYS.ADMIN_USER);
      const path = window.location.pathname || '';
      if (adminToken && !path.includes('/login')) {
        window.localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, adminToken);
        window.location.reload();
        return Promise.reject(error);
      }
      if (!path.includes('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  },
);
